import { FC } from 'react'
import { ClaimData, AttorneyData } from "src/dtos/applicationData"
import {
  FlexDiv,
  ClaimItemFormMainContainer,
  ClaimItemFormSection,
  ClaimItemTitle,
  FormInputTitle,
  ClaimItemFormSectionSubTitle
} from './styles'
import { LeftBar } from '../../basic/LeftBar'
import StandardInput from '../../basic/StandardInput'
import StepBullet from '../../basic/StepBullet'
import StandardCheckbox from '../../basic/StandardCheckbox'
import StandardRadio from '../../basic/StandardRadio'
import StandardFormatInput from '../../basic/StandardFormatInput'
import OptionButton from '../../basic/OptionButton'
import FilePicker from '../../basic/FilePicker'
import { claimTypes } from 'src/constants/application'
import { uploadFiles } from 'src/services/apiClient'
import { validateDateField } from 'src/utils/formDataManipulation'
import { ValidationErrorMessage } from 'src/components/basic/ValidationErrorMessage/ValidationErrorMessage'
import { FILE_SIZE_LABEL } from 'src/utils/constants'

interface ClaimItemFormProps {
  claim: ClaimData;
  claimIndex: number;
  attorneys: AttorneyData[] | undefined;
  setClaim: (data: Partial<ClaimData>) => void;
  isJudge?: boolean;
  isIncorrect?: boolean;
  errorCodes?: string[];
}

const ClaimItemForm: FC<ClaimItemFormProps> = ({ claim, claimIndex, attorneys, setClaim, isJudge, isIncorrect, errorCodes = [] }) => {
  const checkedNotSureCheckbox = (key: keyof ClaimData) => {
    return (claim[key] === undefined || claim[key] === null) ?
      false :
      !claim[key]
  }

  const setNotSureCheckbox = (key: keyof ClaimData, relatedField: keyof ClaimData) => {
    const result = claim[key] === undefined || claim[key] === null ?
      false :
      !claim[key]
    let updated: { [key: string]: any } = { [key]: result }
    if (result === false) updated[relatedField] = undefined
    setClaim(updated)
  }

  const handleAttorneyCheckbox = (attorney: string | undefined) => {
    if (attorney) {
      if (claim?.involvedMembers) {
        const attorneyIndex = claim.involvedMembers.findIndex(att => att === attorney)
        if (attorneyIndex >= 0) {
          setClaim({ involvedMembers: claim.involvedMembers.filter(att => att !== attorney) })
        } else {
          setClaim({ involvedMembers: [...claim.involvedMembers, attorney] })
        }
      } else {
        setClaim({ involvedMembers: [attorney] })
      }
    }
  }

  const handleFileSelect = async (files: File[]) => {
    if (files.length > 0) {
      const response = await uploadFiles(files)
      setClaim({ claimFile: [...(claim?.claimFile ?? []), ...(response ?? [])] })
    } else {
      setClaim({ claimFile: undefined })
    }
  }

  const attorneyName = (attorney: AttorneyData) => {
    return [(attorney?.firstName ?? ''), (attorney?.lastName ?? '')].join(' ')
  }

  const handleInputChange = (key: keyof ClaimData, value: any, relatedField?: keyof ClaimData) => {
    let updated = { [key]: value }
    if (key === 'isClaimClosed') {
      updated = {
        ...updated,
        closingType: undefined,
        totalLossPaid: undefined,
        defenseCostPaid: undefined,
      }
    }
    if (value && relatedField && claim[relatedField] === false) updated = { ...updated, [relatedField]: true }
    setClaim(updated)
  }

  const isDateIncorrect = (value?: string) => {
    if (isIncorrect === true) {
      if (!value) return false

      return !!validateDateField(value)
    }
    return false
  }



  return (
    <ClaimItemFormMainContainer>
      <ClaimItemTitle>
        <h1>Claim {claimIndex + 1}</h1>
        <hr />
      </ClaimItemTitle>
      <FlexDiv gap="18px" margin="48px 0 0 0">
        <LeftBar />
        <FlexDiv gap="60px" dir='column'>

          <ClaimItemFormSection>
            <StepBullet>1</StepBullet>
            <FlexDiv dir='column' gap="16px">
              <FormInputTitle>Firm Name</FormInputTitle>
              <StandardInput
                label=""
                value={claim?.firmName ?? ''}
                onChange={e => handleInputChange('firmName', e.target.value)}
                errorCode={errorCodes[0]}
              />
            </FlexDiv>
          </ClaimItemFormSection>

          {isJudge === true ? null : (
            <ClaimItemFormSection>
              <StepBullet>2</StepBullet>
              <FlexDiv dir='column' gap="16px">
                <FormInputTitle>Individual(s) of firm involved in the claim</FormInputTitle>
                {attorneys?.map((attorney, index) => (
                  <StandardCheckbox
                    key={index}
                    label={attorneyName(attorney)}
                    variation="small"
                    withBg
                    checked={claim?.involvedMembers?.includes(attorneyName(attorney)) ?? false}
                    onChange={() => handleAttorneyCheckbox(attorneyName(attorney))}
                    disabled={claim?.hasInvolvedMembers === false}
                    errorCode={errorCodes[1]}
                  />
                ))}
                <StandardCheckbox
                  label="I’m not sure"
                  variation="small"
                  checked={checkedNotSureCheckbox('hasInvolvedMembers')}
                  onChange={() => setNotSureCheckbox('hasInvolvedMembers', 'involvedMembers')}
                  errorCode={errorCodes[2]}
                />
              </FlexDiv>
            </ClaimItemFormSection>
          )}

          <ClaimItemFormSection>
            <StepBullet>{isJudge === true ? 2 : 3}</StepBullet>
            <FlexDiv dir='column' gap="16px">
              <FormInputTitle>Claimant’s full name</FormInputTitle>
              <StandardInput
                label=""
                value={claim?.claimantFullName ?? ''}
                onChange={e => handleInputChange('claimantFullName', e.target.value, 'hasClaimant')}
                disabled={claim?.hasClaimant === false}
                errorCode={errorCodes[3]}
              />
              <StandardCheckbox
                label="I’m not sure"
                variation="small"
                checked={checkedNotSureCheckbox('hasClaimant')}
                onChange={() => setNotSureCheckbox('hasClaimant', 'claimantFullName')}
                errorCode={errorCodes[4]}
              />
            </FlexDiv>
          </ClaimItemFormSection>

          <ClaimItemFormSection>
            <StepBullet>{isJudge === true ? 3 : 4}</StepBullet>
            <FlexDiv dir='column' gap="16px">
              <FormInputTitle>Claim type</FormInputTitle>
              {claimTypes.map((claimType, index) => (
                <StandardRadio
                  key={index}
                  label={claimType}
                  variant='outlined'
                  checked={claim?.claimType === claimType}
                  onChange={() => handleInputChange('claimType', claimType)}
                  errorCode={errorCodes[5]}
                />
              ))}
            </FlexDiv>
          </ClaimItemFormSection>

          <ClaimItemFormSection>
            <StepBullet>{isJudge === true ? 4 : 5}</StepBullet>
            <FlexDiv dir='column' gap="16px">
              <FormInputTitle>Date of claim</FormInputTitle>
              <StandardFormatInput
                label=""
                maskStyle='date'
                leftIcon='calendar'
                value={claim?.claimDate ?? ''}
                onValueChange={({ formattedValue }) => handleInputChange('claimDate', formattedValue, 'hasClaimDate')}
                disabled={claim?.hasClaimDate === false}
                isIncorrect={isDateIncorrect(claim?.claimDate)}
                errorMessage={isDateIncorrect(claim?.claimDate) ? 'This is not a valid date' : ''}
                errorCode={errorCodes[21]}
              />
              <StandardCheckbox
                label="I’m not sure"
                variation="small"
                checked={checkedNotSureCheckbox('hasClaimDate')}
                onChange={() => setNotSureCheckbox('hasClaimDate', 'claimDate')}
                errorCode={errorCodes[22]}
              />
            </FlexDiv>
          </ClaimItemFormSection>

          <ClaimItemFormSection>
            <StepBullet>{isJudge === true ? 5 : 6}</StepBullet>
            <FlexDiv dir='column' gap="16px">
              <FormInputTitle>Date of alleged error</FormInputTitle>
              <StandardFormatInput
                label=""
                maskStyle='date'
                leftIcon='calendar'
                value={claim?.allegedErrorDate ?? ''}
                onValueChange={({ formattedValue }) => handleInputChange('allegedErrorDate', formattedValue, 'hasAllegedErrorDate')}
                disabled={claim?.hasAllegedErrorDate === false}
                isIncorrect={isDateIncorrect(claim?.allegedErrorDate)}
                errorMessage={isDateIncorrect(claim?.allegedErrorDate) ? 'This is not a valid date' : ''}
                errorCode={errorCodes[23]}
              />
              <StandardCheckbox
                label="I’m not sure"
                variation="small"
                checked={checkedNotSureCheckbox('hasAllegedErrorDate')}
                onChange={() => setNotSureCheckbox('hasAllegedErrorDate', 'allegedErrorDate')}
                errorCode={errorCodes[24]}
              />
            </FlexDiv>
          </ClaimItemFormSection>

          <ClaimItemFormSection>
            <StepBullet>{isJudge === true ? 6 : 7}</StepBullet>
            <FlexDiv dir='column' gap="16px">
              <FormInputTitle>Is this claim closed?</FormInputTitle>
              <FlexDiv gap="12px">
                <OptionButton
                  checked={claim?.isClaimClosed === 'Yes' ?? false}
                  onChange={e => handleInputChange('isClaimClosed', e.target.value)}
                  value="Yes"
                  errorCode={errorCodes[6]}
                >
                  Yes
                </OptionButton>
                <OptionButton
                  checked={claim?.isClaimClosed === 'No' ?? false}
                  onChange={e => handleInputChange('isClaimClosed', e.target.value)}
                  value="No"
                  errorCode={errorCodes[6]}
                >
                  No
                </OptionButton>
                <OptionButton
                  checked={claim?.isClaimClosed === "I'm not sure" ?? false}
                  onChange={e => handleInputChange('isClaimClosed', e.target.value)}
                  value="I'm not sure"
                  errorCode={errorCodes[6]}
                >
                  I'm not sure
                </OptionButton>
              </FlexDiv>
              <ValidationErrorMessage errorCode={errorCodes[6]} />
              {claim?.isClaimClosed === 'Yes' ? (
                <FlexDiv gap="24px">
                  <LeftBar />
                  <FlexDiv dir='column' gap="16px">
                    <FormInputTitle sub>Indicate how the claim was closed</FormInputTitle>
                    <StandardRadio
                      label="Out-of-Court Settlement"
                      variant='outlined'
                      checked={claim?.closingType === 'Out-of-Court Settlement'}
                      onChange={() => handleInputChange('closingType', 'Out-of-Court Settlement')}
                      errorCode={errorCodes[7]}
                    />
                    <StandardRadio
                      label="Court Judgement"
                      variant='outlined'
                      checked={claim?.closingType === 'Court Judgement'}
                      onChange={() => handleInputChange('closingType', 'Court Judgement')}
                      errorCode={errorCodes[7]}
                    />
                    <StandardFormatInput
                      label='Total loss paid including deductibles'
                      maskStyle='number'
                      leftIcon='currency'
                      thousandSeparator=","
                      value={claim?.totalLossPaid ?? ''}
                      onValueChange={({ floatValue }) => handleInputChange('totalLossPaid', floatValue)}
                      errorCode={errorCodes[9]}
                    />
                    <StandardFormatInput
                      label='Defense cost paid'
                      maskStyle='number'
                      leftIcon='currency'
                      thousandSeparator=","
                      value={claim?.defenseCostPaid ?? ''}
                      onValueChange={({ floatValue }) => handleInputChange('defenseCostPaid', floatValue)}
                      errorCode={errorCodes[10]}
                    />
                  </FlexDiv>
                </FlexDiv>
              ) : null}
            </FlexDiv>
          </ClaimItemFormSection>

          <ClaimItemFormSection>
            <StepBullet>{isJudge === true ? 7 : 8}</StepBullet>
            <FlexDiv dir='column' gap="16px">
              <FormInputTitle>Name of insurer</FormInputTitle>
              <StandardInput
                label=""
                value={claim?.insurerName ?? ''}
                onChange={e => handleInputChange('insurerName', e.target.value, 'hasInsurer')}
                disabled={claim?.hasInsurer === false}
                errorCode={errorCodes[17]}
              />
              <StandardCheckbox
                label="I’m not sure"
                variation="small"
                checked={checkedNotSureCheckbox('hasInsurer')}
                onChange={() => setNotSureCheckbox('hasInsurer', 'insurerName')}
              />
            </FlexDiv>
          </ClaimItemFormSection>
          <ClaimItemFormSection maxWidth='640px'>
            <StepBullet>{isJudge === true ? 8 : 9}</StepBullet>
            <FlexDiv dir='column' gap="24px">
              <FlexDiv dir="column" gap="4px">
                <FormInputTitle>Description of Claim</FormInputTitle>
                <ClaimItemFormSectionSubTitle>Provide enough information to allow evaluation.</ClaimItemFormSectionSubTitle>
              </FlexDiv>
              <FlexDiv dir='column' gap="24px">
                <StandardInput
                  label="Alleged act, error, or omission upon which claimant bases claim"
                  rows={6}
                  value={claim?.claimDescription ?? ''}
                  onChange={e => handleInputChange('claimDescription', e.target.value, 'hasClaimDescription')}
                  disabled={claim?.hasClaimDescription === false}
                  errorCode={errorCodes[18]}
                />
                <StandardCheckbox
                  label="I’m not sure"
                  variation="small"
                  checked={checkedNotSureCheckbox('hasClaimDescription')}
                  onChange={() => setNotSureCheckbox('hasClaimDescription', 'claimDescription')}
                />
              </FlexDiv>
              <FlexDiv dir='column' gap="24px" margin='16px 0 0 0'>
                <StandardInput
                  label="Description of case and events, including area of practice out of which the claim arose"
                  rows={6}
                  value={claim?.caseDescription ?? ''}
                  onChange={e => handleInputChange('caseDescription', e.target.value, 'hasCaseDescription')}
                  disabled={claim?.hasCaseDescription === false}
                  errorCode={errorCodes[19]}
                />
                <StandardCheckbox
                  label="I’m not sure"
                  variation="small"
                  checked={checkedNotSureCheckbox('hasCaseDescription')}
                  onChange={() => setNotSureCheckbox('hasCaseDescription', 'caseDescription')}
                />
              </FlexDiv>
              <FlexDiv dir='column' gap="24px" margin='16px 0 0 0'>
                <StandardInput
                  label="Description of the type and extent of injury or damage allegedly sustained"
                  rows={6}
                  value={claim?.damageDescription ?? ''}
                  onChange={e => handleInputChange('damageDescription', e.target.value, 'hasDamageDescription')}
                  disabled={claim?.hasDamageDescription === false}
                  errorCode={errorCodes[20]}
                />
                <StandardCheckbox
                  label="I’m not sure"
                  variation="small"
                  checked={checkedNotSureCheckbox('hasDamageDescription')}
                  onChange={() => setNotSureCheckbox('hasDamageDescription', 'damageDescription')}
                />
              </FlexDiv>
            </FlexDiv>
          </ClaimItemFormSection>

          <ClaimItemFormSection maxWidth='480px'>
            <StepBullet>{isJudge === true ? 9 : 10}</StepBullet>
            <FlexDiv dir='column' gap="16px">
              <FormInputTitle>Upload any applicable claim information (Optional). {FILE_SIZE_LABEL}</FormInputTitle>
              <FilePicker
                accept='.doc,.docx,application/msword, application/pdf'
                onFilesSelect={handleFileSelect}
                selectedFiles={!!claim?.claimFile ? claim.claimFile : []}
                limit={100}
              />
            </FlexDiv>
          </ClaimItemFormSection>
        </FlexDiv>
      </FlexDiv>
    </ClaimItemFormMainContainer>
  )
}

export default ClaimItemForm